'use client'

import { Check } from 'lucide-react'
import { motion, useReducedMotion } from 'motion/react'

import type { RouterOutputs } from '@package/api'
import { typographyVariants } from '@ui/web/lib'
import { formatCurrency } from '@helper/utils/format'
import cn from '@config/tailwind/cn'

type PriceData = RouterOutputs['price']['getActivePrices']

export interface PriceCardGroupProps {
  /** The rate card, already narrowed to the buyer's tier by the caller. */
  getAllPrice: PriceData
  /** Controlled selection — the id of the row the buyer picked. */
  value: string
  onValueChange: (price: PriceData[number]) => void
  disabled?: boolean
  className?: string
}

/**
 * How much cheaper per seat per month this row is than the dearest row on offer, as a fraction.
 *
 * The same comparison `SelectPrice` made in its "Hemat x%" note, kept so the two controls cannot
 * quote different percentages for the same rate card.
 */
function savingsFraction(dearest: PriceData[number], row: PriceData[number]): number {
  if (row.price === dearest.price) return 0

  return (dearest.price - row.price) / dearest.price
}

const PERCENT_FORMATTER = new Intl.NumberFormat('id-ID', { style: 'percent', maximumFractionDigits: 0 })

/**
 * The billing term as a ROW of radio cards, one per term on offer.
 *
 * Replaces the collapsed `SelectPrice` dropdown. The terms differ by three numbers at once —
 * length, rate per seat per month, and what that rate saves — and a dropdown shows one of them at a
 * time, so the buyer had to open it and compare from memory. Side by side, the comparison is the
 * layout: three short columns read against each other in one glance, where a stack read as a list
 * to work down.
 *
 * Wraps on a narrow screen rather than scrolling sideways — `basis-[8rem]` with `flex-1` keeps two
 * or three per line on a phone instead of one very wide card each.
 *
 * Built from buttons rather than a `RadioGroup` primitive because `@ui/web` ships none; the ARIA
 * roles are set by hand so the group still reads as a radio group to a screen reader.
 */
export function PriceCardGroup({ getAllPrice, value, onValueChange, disabled, className }: PriceCardGroupProps) {
  const reduceMotion = useReducedMotion()

  if (getAllPrice.length === 0) return null

  const dearest = getAllPrice.reduce((held, current) => (current.price > held.price ? current : held))

  return (
    <div role='radiogroup' aria-label='Paket' className={cn('srd:flex srd:flex-wrap srd:gap-2', className)}>
      {getAllPrice.map((price) => {
        const isSelected = price.id === value
        const savings = savingsFraction(dearest, price)

        return (
          <motion.button
            key={price.id}
            type='button'
            role='radio'
            aria-checked={isSelected}
            disabled={disabled}
            onClick={() => onValueChange(price)}
            /* The chosen card sits a touch proud of the others. 1.03 is small enough to read as
               emphasis rather than as the card growing — it is the only thing that moves here. */
            animate={reduceMotion ? undefined : { scale: isSelected ? 1.03 : 1 }}
            whileTap={disabled || reduceMotion ? undefined : { scale: isSelected ? 1.0 : 0.98 }}
            transition={{ type: 'spring', stiffness: 420, damping: 30 }}
            className={cn(
              'srd:relative srd:flex srd:min-w-0 srd:flex-1 srd:basis-[8rem] srd:flex-col srd:gap-y-1 srd:rounded-lg srd:border srd:p-3 srd:text-left',
              'srd:focus-visible:ring-blue-30 srd:focus-visible:ring-2 srd:focus-visible:ring-offset-2 srd:focus-visible:outline-none',
              'srd:disabled:cursor-not-allowed srd:disabled:opacity-50',
              'srd:min-w-[135px]',
              isSelected
                ? 'srd:border-blue-100 srd:bg-blue-10 srd:z-10'
                : 'srd:border-outline-variant srd:bg-neutral-white srd:hover:border-blue-50',
            )}
          >
            <span className='srd:flex srd:items-center srd:justify-between srd:gap-x-2'>
              <span className={cn(typographyVariants({ variant: 'label-md' }), 'srd:text-neutral-black')}>
                {price.duration} bulan
              </span>

              {/* The tick, not a ring: the selected card is already carried by its border and fill,
                  and a second empty circle on every card reads as a checklist. */}
              {isSelected && <Check className='srd:text-blue-100 srd:size-4 srd:shrink-0' />}
            </span>

            {price.isFavorite && (
              <span
                className={cn(
                  typographyVariants({ variant: 'label-sm' }),
                  'srd:bg-purple-100 srd:text-neutral-white srd:w-fit srd:rounded-sm srd:px-1 srd:py-0.5',
                )}
              >
                Populer
              </span>
            )}

            <span className={cn(typographyVariants({ variant: 'label-md' }), 'srd:text-neutral-black')}>
              {formatCurrency(price.price)}
              <span className={cn(typographyVariants({ variant: 'body-sm' }), 'srd:text-grey-100')}> /kursi/bln</span>
            </span>

            {savings > 0 && (
              <span className={cn(typographyVariants({ variant: 'label-sm' }), 'srd:text-purple-100')}>
                Hemat {PERCENT_FORMATTER.format(savings)}
              </span>
            )}
          </motion.button>
        )
      })}
    </div>
  )
}
